/* eslint-disable no-shadow */
/* eslint-disable prefer-exponentiation-operator */
import { ApolloError } from "@apollo/client";

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
  CRITICAL = "critical",
}

/**
 * Error types for categorization
 */
export enum ErrorType {
  NETWORK = "network",
  GRAPHQL = "graphql",
  AUTHENTICATION = "authentication",
  AUTHORIZATION = "authorization",
  VALIDATION = "validation",
  RUNTIME = "runtime",
  UNKNOWN = "unknown",
}

/**
 * Standardized error interface
 */
export interface AppError {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  originalError?: Error | ApolloError;
  timestamp: Date;
  context?: Record<string, unknown>;
}

/**
 * Error handler options
 */
export interface ErrorHandlerOptions {
  silent?: boolean; // Don't show toast notification
  logToSentry?: boolean; // Log to Sentry (default: true)
  showErrorBoundary?: boolean; // Show error boundary fallback
  context?: Record<string, unknown>; // Additional context
}

/**
 * Retry configuration for failed requests
 */
export interface RetryConfig {
  maxRetries: number;
  initialDelay: number; // in milliseconds
  maxDelay: number; // in milliseconds
  backoffMultiplier: number;
  retryableErrors: ErrorType[];
}

/**
 * Default retry configuration with exponential backoff
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryableErrors: [ErrorType.NETWORK],
};

/**
 * Error messages in Slovak
 */
export const ERROR_MESSAGES_SK = {
  network: {
    offline: "Nie ste pripojený k internetu",
    timeout: "Požiadavka trvala príliš dlho",
    serverDown: "Server je momentálne nedostupný",
    unknown: "Nastala chyba pri komunikácii so serverom",
  },
  authentication: {
    tokenExpired: "Vaše prihlásenie vypršalo. Prihláste sa prosím znova.",
    invalidCredentials: "Neplatné prihlasovacie údaje",
    unauthorized: "Nemáte oprávnenie na túto akciu",
  },
  validation: {
    required: "Toto pole je povinné",
    invalid: "Neplatný formát",
    unknown: "Validačná chyba",
  },
  runtime: {
    componentCrash: "Nastala neočakávaná chyba",
    unknown: "Niečo sa pokazilo",
  },
  general: {
    unknown: "Nastala neočakávaná chyba",
    tryAgain: "Skúste to prosím znova",
    contactSupport: "Ak problém pretrváva, kontaktujte podporu",
  },
};

/**
 * Parse Apollo/GraphQL error to AppError
 */
export function parseApolloError(error: ApolloError): AppError {
  // Network errors
  if (error.networkError) {
    const networkError = error.networkError as Error & { statusCode?: number };

    // Check if offline
    if (!navigator.onLine) {
      return {
        type: ErrorType.NETWORK,
        severity: ErrorSeverity.ERROR,
        message: ERROR_MESSAGES_SK.network.offline,
        originalError: error,
        timestamp: new Date(),
      };
    }

    // Server error (5xx)
    if (networkError.statusCode && networkError.statusCode >= 500) {
      return {
        type: ErrorType.NETWORK,
        severity: ErrorSeverity.ERROR,
        message: ERROR_MESSAGES_SK.network.serverDown,
        originalError: error,
        timestamp: new Date(),
      };
    }

    // Auth errors (401)
    if (networkError.statusCode === 401) {
      return {
        type: ErrorType.AUTHENTICATION,
        severity: ErrorSeverity.ERROR,
        message: ERROR_MESSAGES_SK.authentication.tokenExpired,
        originalError: error,
        timestamp: new Date(),
      };
    }

    // Authorization errors (403)
    if (networkError.statusCode === 403) {
      return {
        type: ErrorType.AUTHORIZATION,
        severity: ErrorSeverity.ERROR,
        message: ERROR_MESSAGES_SK.authentication.unauthorized,
        originalError: error,
        timestamp: new Date(),
      };
    }

    return {
      type: ErrorType.NETWORK,
      severity: ErrorSeverity.ERROR,
      message: ERROR_MESSAGES_SK.network.unknown,
      originalError: error,
      timestamp: new Date(),
    };
  }

  // GraphQL errors
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    const firstError = error.graphQLErrors[0];
    const extensions = firstError.extensions || {};

    // Check for auth/authorization errors in extensions
    if (extensions.code === "UNAUTHENTICATED") {
      return {
        type: ErrorType.AUTHENTICATION,
        severity: ErrorSeverity.ERROR,
        message: ERROR_MESSAGES_SK.authentication.tokenExpired,
        originalError: error,
        timestamp: new Date(),
      };
    }

    if (extensions.code === "FORBIDDEN") {
      return {
        type: ErrorType.AUTHORIZATION,
        severity: ErrorSeverity.ERROR,
        message: ERROR_MESSAGES_SK.authentication.unauthorized,
        originalError: error,
        timestamp: new Date(),
      };
    }

    // Validation errors
    if (extensions.code === "BAD_USER_INPUT") {
      return {
        type: ErrorType.VALIDATION,
        severity: ErrorSeverity.WARNING,
        message: firstError.message || ERROR_MESSAGES_SK.validation.unknown,
        originalError: error,
        timestamp: new Date(),
      };
    }

    return {
      type: ErrorType.GRAPHQL,
      severity: ErrorSeverity.ERROR,
      message: firstError.message,
      originalError: error,
      timestamp: new Date(),
    };
  }

  // Unknown error
  return {
    type: ErrorType.UNKNOWN,
    severity: ErrorSeverity.ERROR,
    message: error.message || ERROR_MESSAGES_SK.general.unknown,
    originalError: error,
    timestamp: new Date(),
  };
}

/**
 * Parse generic JavaScript error to AppError
 */
export function parseError(error: Error): AppError {
  return {
    type: ErrorType.RUNTIME,
    severity: ErrorSeverity.ERROR,
    message: error.message || ERROR_MESSAGES_SK.runtime.unknown,
    originalError: error,
    timestamp: new Date(),
  };
}

/**
 * Calculate retry delay with exponential backoff
 */
export function calculateRetryDelay(
  attemptNumber: number,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
): number {
  const delay = config.initialDelay * (config.backoffMultiplier ** attemptNumber);
  return Math.min(delay, config.maxDelay);
}

/**
 * Check if error is retryable
 */
export function isRetryableError(
  error: AppError,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
): boolean {
  return config.retryableErrors.includes(error.type);
}
